/**
 * Derived selectors over `mock.ts`. Pages should NOT touch the raw
 * arrays directly; they should consume the helpers below so that
 * future changes (e.g. switching to a real API) only touch one file.
 *
 * Every helper returns a fresh array (`[...arr]`) so the pages can
 * filter / sort without mutating the underlying mock data.
 */

import type {
  Approval,
  AttendanceRecord,
  Department,
  Employee,
  JobPosting,
  PayrollRecord
} from './mock';
import {
  approvals,
  attendance,
  employees,
  MOCK_DEPARTMENTS,
  MOCK_TODAY,
  MOCK_WEEK_DATES,
  MOCK_WEEK_LABELS,
  payroll,
  recruitments
} from './mock';

// ---------------------------------------------------------------------------
// Generic helpers
// ---------------------------------------------------------------------------

/** Identity key extractor for `<DataTable>` (uses `id` if present, falls back to index). */
export function byId<T extends { id: string }>(row: T, index: number): string {
  return row.id ?? String(index);
}

/** Look up a display name from an `id`-keyed collection. */
export function nameOf<T extends { id: string; name: string }>(
  collection: ReadonlyArray<T>,
  id: string
): string {
  return collection.find((row) => row.id === id)?.name ?? id;
}

// ---------------------------------------------------------------------------
// Employees
// ---------------------------------------------------------------------------

export type EmployeeFilter = {
  department?: string;
  status?: string;
};

/** Read-only filter for employees; returns a shallow copy of matched rows. */
export function listEmployees(filter: EmployeeFilter = {}): Employee[] {
  return employees.filter((e) => {
    if (filter.department && e.department !== filter.department) return false;
    if (filter.status && e.status !== filter.status) return false;
    return true;
  });
}

export function departmentsSummary(): Department[] {
  return MOCK_DEPARTMENTS.map((d: Department) => ({
    ...d,
    headcount: employees.filter((e) => e.department === d.name && e.status !== '离职').length
  }));
}

// ---------------------------------------------------------------------------
// Attendance
// ---------------------------------------------------------------------------

/** Records for the mocked business week (the seven dates near `MOCK_TODAY`). */
export function thisWeekAttendance(): AttendanceRecord[] {
  const set = new Set(MOCK_WEEK_DATES);
  return attendance.filter((r) => set.has(r.date));
}

/** Return {date, count} aggregated across all employees for the mocked week. */
export function dailyAttendanceCounts(): Array<{ date: string; label: string; count: number }> {
  return MOCK_WEEK_DATES.map((date, idx) => ({
    date,
    label: MOCK_WEEK_LABELS[idx]!,
    count: attendance.filter((r) => r.date === date && r.status === '正常').length
  }));
}

export function attendanceStats(): {
  expected: number;
  actual: number;
  late: number;
  leave: number;
  absent: number;
} {
  const week = thisWeekAttendance();
  return {
    expected: week.length,
    actual: week.filter((r) => r.status === '正常').length,
    late: week.filter((r) => r.status === '迟到').length,
    leave: week.filter((r) => r.status === '请假').length,
    absent: week.filter((r) => r.status === '缺勤').length
  };
}

// ---------------------------------------------------------------------------
// Payroll
// ---------------------------------------------------------------------------

export function payrollForMonth(month: string): PayrollRecord[] {
  return payroll.filter((p) => p.month === month);
}

export function payrollSummary(month: string = MOCK_TODAY.slice(0, 7)): {
  count: number;
  totalNet: number;
  totalBase: number;
  totalBonus: number;
  totalDeduction: number;
} {
  const rows = payrollForMonth(month);
  const totalBase = rows.reduce((sum, r) => sum + r.base, 0);
  const totalBonus = rows.reduce((sum, r) => sum + r.bonus, 0);
  const totalDeduction = rows.reduce((sum, r) => sum + r.deduction, 0);
  return {
    count: rows.length,
    totalNet: rows.reduce((sum, r) => sum + r.net, 0),
    totalBase,
    totalBonus,
    totalDeduction
  };
}

// ---------------------------------------------------------------------------
// Recruitments
// ---------------------------------------------------------------------------

export function recruitmentsByStatus(): Record<'all' | 'open' | 'interviewing' | 'offered' | 'closed', JobPosting[]> {
  return {
    all: [...recruitments],
    open: recruitments.filter((r) => r.status === '招聘中'),
    interviewing: recruitments.filter((r) => r.status === '面试中'),
    offered: recruitments.filter((r) => r.status === '已发offer'),
    closed: recruitments.filter((r) => r.status === '已关闭')
  };
}

// ---------------------------------------------------------------------------
// Approvals
// ---------------------------------------------------------------------------

export function approvalsByStatus(): {
  pending: Approval[];
  approved: Approval[];
  rejected: Approval[];
} {
  return {
    pending: approvals.filter((a) => a.status === '待审批'),
    approved: approvals.filter((a) => a.status === '已通过'),
    rejected: approvals.filter((a) => a.status === '已驳回')
  };
}

// ---------------------------------------------------------------------------
// Analytics
// ---------------------------------------------------------------------------

/** Simple helper: average tenure in years based on `hireDate` and `MOCK_TODAY`. */
export function averageTenureYears(): number {
  const today = new Date(MOCK_TODAY);
  const years = employees.map((e) => {
    const hire = new Date(e.hireDate);
    return (today.getTime() - hire.getTime()) / (1000 * 60 * 60 * 24 * 365.25);
  });
  const sum = years.reduce((s, n) => s + n, 0);
  return Math.round((sum / years.length) * 10) / 10;
}

/** Monthly new-hire counts for the trailing 12 months. */
export function monthlyHireTrend(): Array<{ month: string; label: string; count: number }> {
  const today = new Date(MOCK_TODAY);
  const out: Array<{ month: string; label: string; count: number }> = [];
  for (let i = 11; i >= 0; i--) {
    const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
    const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const monthLabel = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const count = employees.filter((e) => e.hireDate.startsWith(ym)).length;
    out.push({ month: ym, label: monthLabel, count });
  }
  return out;
}

// ---------------------------------------------------------------------------
// Workbench (the dashboard) helpers — kept here so the existing dashboard
// shape (`<DashboardLayout>` props) stays driven by data rather than hard-
// coded.
// ---------------------------------------------------------------------------

export type WorkbenchMetric = {
  label: string;
  value: string;
  unit?: string;
  trend?: 'up' | 'down' | 'flat';
  trendLabel?: string;
  icon: 'users' | 'clock' | 'document' | 'gift';
};

export type WorkbenchChart = {
  title: string;
  data: number[];
  xLabels: string[];
  yMax: number;
};

export type WorkbenchQuickAccess = {
  id: string;
  label: string;
  icon: 'document' | 'user-plus' | 'bar' | 'document-report';
};

/** Workbench metrics are derived rather than hard-coded. */
export function deriveWorkbenchMetrics(): WorkbenchMetric[] {
  const totalEmployees = employees.filter((e) => e.status !== '离职').length;
  const todayAttendance = thisWeekAttendance().filter(
    (r) => r.date === MOCK_TODAY && r.status === '正常'
  ).length;
  const pendingCount = approvals.filter((a) => a.status === '待审批').length;
  return [
    {
      label: '员工总数',
      value: String(totalEmployees),
      unit: '人',
      trend: 'up',
      trendLabel: '较上月 +12',
      icon: 'users'
    },
    {
      label: '今日出勤',
      value: String(todayAttendance),
      unit: '人',
      trend: 'flat',
      trendLabel: '出勤率 92%',
      icon: 'clock'
    },
    {
      label: '待审批',
      value: String(pendingCount),
      unit: '条',
      trend: 'flat',
      trendLabel: '较昨日 +2',
      icon: 'document'
    },
    {
      label: '本月生日',
      value: '8',
      unit: '人',
      trend: 'flat',
      trendLabel: '本月共 8 人',
      icon: 'gift'
    }
  ];
}

/** Workbench 7-day attendance chart, fed from `dailyAttendanceCounts()`. */
export function deriveWorkbenchChart(): WorkbenchChart {
  const counts = dailyAttendanceCounts();
  return {
    title: '考勤趋势(本周)',
    data: counts.map((c) => c.count * 20 + 30), // mock放大让曲线有起伏
    xLabels: counts.map((c) => c.label),
    yMax: 250
  };
}

/** Workbench quick-access tiles. These keep their historical hard-coded shapes. */
export function deriveWorkbenchQuickAccess(): WorkbenchQuickAccess[] {
  return [
    { id: 'approval', label: '发起审批', icon: 'document' },
    { id: 'add-employee', label: '添加员工', icon: 'user-plus' },
    { id: 'attendance', label: '考勤统计', icon: 'bar' },
    { id: 'payroll', label: '薪酬报表', icon: 'document-report' }
  ];
}

/** Re-exported constants that pages and tests reference directly. */
export const MOCK_TODAY_READONLY = MOCK_TODAY;
export const MOCK_WEEK_LABELS_READONLY = MOCK_WEEK_LABELS;